import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

export default function StyledSelect({
  value,
  onChange,
  options,
  placeholder = 'Select option',
  disabled = false,
  searchable = false,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const activeOption = useMemo(
    () => options.find((option) => String(option.value) === String(value)) || null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((option) => String(option.label).toLowerCase().includes(q));
  }, [options, query, searchable]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className="w-full h-12 rounded-xl border border-slate-200 bg-white px-4 text-left text-slate-900 shadow-sm transition hover:border-rose-200 focus:outline-none focus:ring-4 focus:ring-rose-100 focus:border-rose-300 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className={`block truncate pr-7 text-sm ${activeOption ? 'text-slate-900' : 'text-slate-400'}`}>
          {activeOption ? activeOption.label : placeholder}
        </span>
        <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>

      {open ? (
        <div className="absolute z-50 mt-2 w-full rounded-2xl border border-slate-200 bg-white shadow-[0_18px_40px_rgba(15,23,42,0.16)] overflow-hidden animate-[fadeIn_140ms_ease]">
          {searchable ? (
            <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/80">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full h-9 rounded-lg border border-slate-200 bg-white pl-8 pr-2 text-sm text-slate-700 outline-none focus:ring-2 focus:ring-rose-100"
                />
              </div>
            </div>
          ) : null}

          <div className="max-h-52 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-slate-400">No options found</p>
            ) : (
              filteredOptions.map((option) => {
                const selected = String(option.value) === String(value);
                return (
                  <button
                    key={`${option.value}`}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                      className={`w-full px-3 py-2.5 text-sm text-left flex items-center justify-between transition ${selected ? 'bg-rose-50 text-rose-700 font-medium' : 'text-slate-700 hover:bg-slate-50'}`}
                  >
                    <span className="truncate">{option.label}</span>
                    {selected ? <Check className="w-4 h-4" /> : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
